import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ContactarExitoModal } from "@/features/simulador/components/ContactarExitoModal";
import { DescargaModal } from "@/features/simulador/components/DescargaModal";
import { SolicitudActivaModal } from "@/features/simulador/components/SolicitudActivaModal";
import { useSimulador } from "@/features/simulador/hooks/useSimulador";
import { BarChart } from "@/shared/components/BarChart";
import { formatRate } from "@/shared/utils/formatters";

function formatMoney(value: number | null | undefined): string {
  if (value == null) return "$0";
  return new Intl.NumberFormat("es-EC", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function round2(value: number | null | undefined): number | null {
  if (value == null) return null;
  return Math.round(value * 100) / 100;
}

function buildSimuladorChartData(
  resultado: NonNullable<ReturnType<typeof useSimulador>["resultado"]>,
  lang: string,
) {
  const formatMonth = (dateString: string | null): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return '';

    return new Intl.DateTimeFormat(lang === 'es' ? 'es-EC' : 'en-US', {
      month: 'short',
      timeZone: 'UTC',
    })
      .format(new Date(Date.UTC(year, month - 1, day)))
      .replace('.', '');
  };

  return resultado.proyeccion.map((c, i) => {
    // Los importes ya vienen calculados por API REST Dynamics vía BFF.
    // Aquí solo se transforman al formato visual que consume BarChart.
    // Bono pagado en este período: es puntual/anual. No usar bonoAcumulado,
    // porque ese campo se repite en cada período posterior al pago.
    const bonoDelPeriodo = c.bonoAnual ?? 0;

    return {
      label: formatMonth(c.fechaVencimiento) || `M${i + 1}`,
      value: c.valorAcumuladoSinBono,
      bonus: bonoDelPeriodo > 0 ? bonoDelPeriodo : undefined,
      total: c.valorAcumulado,
    };
  });
}

const MONTOS_FIJOS = [
  10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000, 60000, 75000,
  100000,
];

const MONTO_MAX = 9999999;

export function SimuladorPage() {
  const { t, i18n } = useTranslation("simulador");
  const [showDescargaModal, setShowDescargaModal] = useState(false);
  const {
    grupoActual,
    configuracionSeleccionada,
    userHasSelectedConfig,
    plazoSeleccionado,
    userHasSelectedPlazo,
    plazosUnicos,
    montoMin,
    montoPreset,
    setMontoPresetAndMonto,
    setMontoManual,
    selectPlazo,
    monto,
    resultado,
    calculando,
    error,
    seleccionarConfiguracion,
    calcular,
    puedeCalcular,
    contactando,
    showExitoModal,
    contactarError,
    handleContactar,
    closeExitoModal,
    showSolicitudActivaModal,
    solicitudActivaMensaje,
    closeSolicitudActivaModal,
    descargando,
    descargarError,
    handleDescargar,
  } = useSimulador();

  const inversionNeta = resultado ? resultado.montoInvertido : 0;
  const rentaPorPeriodo =
    resultado?.proyeccion.find((c) => c.rendimiento > 0)?.rendimiento ?? 0;
  const bonoAnualImporte =
    resultado?.proyeccion.find((cuota) => (cuota.bonoAcumulado ?? 0) > 0)?.bonoAcumulado ?? 0;

  const chartData = resultado
    ? buildSimuladorChartData(
        resultado,
        i18n.language?.split('-')[0] ?? 'es',
      ).map(
        (d) => ({
          label: d.label,
          value: d.value,
          bonus: d.bonus,
        }),
      )
    : [];

  const chartMax =
    chartData.length > 0
      ? Math.max(...chartData.map((d) => d.value + (d.bonus ?? 0)))
      : 0;

  const yLabels =
    chartMax > 0
      ? [
          `$${(chartMax / 1000).toFixed(1)}k`,
          `$${((inversionNeta + chartMax) / 2 / 1000).toFixed(1)}k`,
          `$${(inversionNeta / 1000).toFixed(1)}k`,
        ]
      : ["$0,0k", "$0,0k", "$0,0k"];

  return (
    <>
      <div className="grid items-stretch gap-4 pt-0 pr-[1.5%] pb-0 pl-2 mt-auto mb-auto lg:grid-cols-[minmax(20rem,auto)_minmax(0,1fr)] max-lg:grid-cols-1">
        <div className="flex min-w-80 flex-col gap-3 rounded-md bg-[#E0E0E01A] p-4 shadow-card">
          <h2 className="m-0 text-[1.25rem] font-bold text-text-primary">
            {t("filterTitle")}
          </h2>

          {grupoActual && (
            <div className="flex flex-col gap-2 py-3">
              <span className="text-[0.75rem] font-bold uppercase tracking-[0.08em] text-text-muted">
                {t("periodicidad")}
              </span>
              <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2">
                {grupoActual.configuraciones.map((c) => (
                  <button
                    key={c.codigo}
                    type="button"
                    className={`cursor-pointer rounded-sm border px-1 py-1 text-center text-body-3 font-medium transition-colors duration-150 ${
                      userHasSelectedConfig &&
                      configuracionSeleccionada?.codigo === c.codigo
                        ? "border-[#0066AA] bg-[#0066AA] text-white"
                        : "border-[#008EEE] bg-transparent text-text-muted hover:border-[#0066AA] hover:text-[#0066AA]"
                    }`}
                    onClick={() => seleccionarConfiguracion(c)}
                  >
                    {t(`productoNombres.${c.periodicidadNombre}`, {
                      defaultValue: c.periodicidadNombre,
                    })}
                  </button>
                ))}
              </div>
            </div>
          )}

          {plazosUnicos.length > 0 && (
            <div className="flex flex-col gap-2 py-3">
              <span className="text-[0.75rem] font-bold uppercase tracking-[0.08em] text-text-muted">
                {t("plazo")}
              </span>
              <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2">
                {plazosUnicos.map((p) => (
                  <button
                    key={p.plazo}
                    type="button"
                    className={`cursor-pointer rounded-sm border px-1 py-1 text-center text-body-3 font-medium transition-colors duration-150 ${
                      userHasSelectedPlazo && plazoSeleccionado === p.plazo
                        ? "border-[#0066AA] bg-[#0066AA] text-white"
                        : "border-[#008EEE] bg-transparent text-text-muted hover:border-[#0066AA] hover:text-[#0066AA]"
                    }`}
                    onClick={() => selectPlazo(p.plazo)}
                  >
                    {t("plazoValue", { months: p.plazo })}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 py-3">
            <span className="text-[0.75rem] font-bold uppercase tracking-[0.08em] text-text-muted">
              {t("monto")}
            </span>

            <div className="grid grid-cols-3 gap-6 max-md:grid-cols-2">
              {MONTOS_FIJOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`cursor-pointer rounded-sm border px-2 py-2 text-center text-body-3 font-medium transition-colors duration-150 ${
                    montoPreset === m
                      ? "border-[#0066AA] bg-[#0066AA] text-white"
                      : "border-[#008EEE] bg-transparent text-text-muted hover:border-[#0066AA] hover:text-[#0066AA]"
                  }`}
                  onClick={() => setMontoPresetAndMonto(m)}
                >
                  {m / 1000}k
                </button>
              ))}
            </div>

            <input
              id="monto-input"
              type="number"
              min={montoMin}
              max={MONTO_MAX}
              step={1}
              className="w-full rounded-sm border-[1.5px] border-border-soft bg-surface-page px-4 py-3 text-body-2 text-text-primary outline-none focus:border-brand-primary focus:shadow-[0_0_0_3px_var(--color-focus-ring)]"
              placeholder="10.000"
              value={monto}
              onKeyDown={(e) => {
                if ([".", ",", "e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setMontoManual("");
                  return;
                }
                const num = parseInt(raw, 10);
                if (Number.isNaN(num)) return;
                setMontoManual(String(Math.min(num, MONTO_MAX)));
              }}
              onBlur={(e) => {
                const num = parseInt(e.target.value, 10);
                if (Number.isNaN(num) || num < montoMin) {
                  setMontoManual(String(montoMin));
                }
              }}
            />

            <input
              id="monto-slider"
              type="range"
              min={montoMin}
              max={MONTO_MAX}
              step={1000}
              className="w-full cursor-pointer accent-brand-primary"
              value={
                monto !== ""
                  ? Math.min(parseFloat(monto) || montoMin, MONTO_MAX)
                  : montoMin
              }
              onChange={(e) => {
                const val = Number(e.target.value);
                MONTOS_FIJOS.includes(val)
                  ? setMontoPresetAndMonto(val)
                  : setMontoManual(e.target.value);
              }}
            />

            <div className="flex items-center justify-between text-[0.75rem]">
              <span className="font-bold text-brand-primary">
                {monto !== "" && !Number.isNaN(parseFloat(monto))
                  ? `${parseFloat(monto) / 1000}k`
                  : `${montoMin / 1000}k`}
              </span>
            </div>
            <span className="mt-0.5 block text-left text-[0.75rem] text-text-muted">
              {t("montoMin", { amount: formatMoney(montoMin) })}
            </span>
          </div>

          {error && (
            <p className="m-0 text-[0.875rem] text-[#c0392b]">{error}</p>
          )}

          <button
            type="button"
            className="mt-auto w-full cursor-pointer rounded-sm bg-[#0066AA] p-4 text-body-2 font-bold text-white transition-colors duration-150 hover:bg-[#005a99] disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-[#B0BEC5] disabled:text-white/60"
            onClick={() => void calcular()}
            disabled={!puedeCalcular}
          >
            {calculando ? t("calculating") : t("calculate")}
          </button>
        </div>

        <div className="flex min-w-0 flex-col gap-3 bg-[#E0E0E01A] pl-4">
          <h2 className="m-0 text-[1.25rem] font-normal text-black">
            {t("intro")}
          </h2>

          <h3 className="m-0 text-[1.25rem] font-bold text-black">
            {t("growthTitle")}
          </h3>
          <p className="m-0 text-body-3 leading-[1.6] text-black">
            {t("growthDesc")}
            <br />
            {t("growthDisclaimer")}
          </p>

          <div
            className={`flex flex-col gap-1 rounded-[6px] px-5 py-2 transition-colors duration-300 ${
              resultado && !calculando
                ? "bg-[linear-gradient(180deg,#008EEE_0%,#0066AA_37.5%,#003D66_100%)]"
                : "bg-[#bdbdbd]"
            }`}
          >
            <span className="text-[0.8125rem] font-normal tracking-[0.01em] text-white">
              {(() => {
                const raw =
                  resultado?.productoNombre ?? grupoActual?.nombre ?? "";
                return raw
                  ? t(`productoNombres.${raw}`, { defaultValue: raw })
                  : t("defaultProductName");
              })()}
            </span>
            <div className="flex items-stretch gap-5">
              <div className="flex flex-1 flex-col justify-center gap-1">
                <span className="text-[0.75rem] text-white">
                  {t("interestRate")}{" "}
                  <strong className="font-normal text-white">
                    {resultado ? `${formatRate(resultado.tasaAnual)}%` : "0.00%"}
                  </strong>
                </span>
              </div>
              <div className="block w-px shrink-0 self-stretch bg-white/50" />
              <div className="flex flex-1 flex-col justify-center gap-1">
                <span className="text-[0.75rem] text-white">
                  {t("rentaMensual")}{" "}
                  <strong className="font-normal text-white">
                    {resultado ? formatMoney(round2(rentaPorPeriodo)) : "$0"}
                  </strong>
                </span>
                <span className="text-[0.75rem] text-white">
                  {t("bonoAnual")}{" "}
                  <strong className="font-normal text-white">
                    {resultado ? formatMoney(round2(bonoAnualImporte)) : "$0"}
                  </strong>
                </span>
              </div>
              <div className="block w-px shrink-0 self-stretch bg-white/50" />
              <div className="flex flex-1 flex-col justify-center gap-1">
                <span className="text-[0.75rem] text-white">
                  {t("finalReturn")}{" "}
                  <strong className="text-[0.875rem] text-white">
                    {resultado
                      ? formatMoney(round2(resultado.valorFinal))
                      : "$0"}
                  </strong>
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col rounded-md bg-gradient-to-b from-white to-[#EEF7FC] p-4">
            <p className="m-0 mb-4 text-center text-[1.0625rem] font-semibold text-[#104063]">
              {t("chartTitle")}
            </p>
            <div className="flex min-w-0 items-start gap-2 overflow-x-auto overflow-y-visible pb-6">
              <div className="shrink-0 pb-5">
                <div className="flex h-48 flex-col items-end justify-between text-[0.7rem] text-text-muted">
                  {(chartData.length > 0
                    ? yLabels
                    : ["$0.0k", "$0.0k", "$0.0k"]
                  ).map((label, i) => (
                    <span key={i}>{label}</span>
                  ))}
                </div>
              </div>
              <div className="min-w-max flex-1 overflow-visible">
                {chartData.length > 0 ? (
                  <BarChart
                    data={chartData}
                    min={inversionNeta}
                    ariaLabel={t("chartTitle")}
                  />
                ) : (
                  <div className="flex h-48 gap-3" aria-hidden="true">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex flex-1 flex-col items-center justify-end gap-1"
                      >
                        <span className="text-[0.625rem] font-medium uppercase tracking-[0.04em] text-text-muted">
                          Mes
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-auto mb-4 flex flex-wrap justify-center gap-4 pt-[4%]">
            <button
              type="button"
              className="flex h-12 w-60 cursor-pointer items-center justify-center rounded-sm bg-[#0066AA] px-6 text-[0.95rem] font-semibold text-white transition-colors duration-150 hover:bg-[#005a99] hover:text-brand-primary disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-[#b0b0b0] disabled:text-white"
              onClick={() => void handleContactar()}
              disabled={!resultado || calculando || contactando}
            >
              {contactando ? t("sending") : t("contact")}
            </button>
            <button
              type="button"
              className="flex h-12 w-60 cursor-pointer items-center justify-center rounded-sm border border-[#008EEE] px-6 text-[0.95rem] font-semibold text-[#008EEE] transition-colors duration-150 hover:border-brand-primary hover:text-brand-primary disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-[#b0b0b0] disabled:text-[#b0b0b0]"
              onClick={() => setShowDescargaModal(true)}
              disabled={!resultado || calculando || descargando}
            >
              {descargando ? t("downloading") : t("download")}
            </button>
          </div>

          {contactarError && (
            <p className="m-0 text-center text-[0.875rem] text-[#c0392b]">
              {contactarError}
            </p>
          )}
          {descargarError && (
            <p className="m-0 text-center text-[0.875rem] text-[#c0392b]">
              {descargarError}
            </p>
          )}
        </div>
      </div>

      {showExitoModal && <ContactarExitoModal onClose={closeExitoModal} />}

      {showSolicitudActivaModal && solicitudActivaMensaje && (
        <SolicitudActivaModal
          mensaje={solicitudActivaMensaje}
          onClose={closeSolicitudActivaModal}
        />
      )}

      {showDescargaModal && resultado && (
        <DescargaModal
          productoNombre={resultado.productoNombre}
          descargando={descargando}
          onDescargar={() => void handleDescargar()}
          onClose={() => setShowDescargaModal(false)}
        />
      )}
    </>
  );
}
